import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Search, Filter, Clock, CheckCircle, Edit3, AlertCircle } from 'lucide-react';
import { useI18n } from '../i18n';

import { API_BASE } from '../config/api';

interface Encounter {
    id: string;
    status: string;
    subjective: string;
    chiefComplaint: string | null;
    createdAt: string;
    signedAt: string | null;
    patient: { id: string; firstName: string; lastName: string };
    doctor: { id: string; firstName: string; lastName: string; specialty: string };
    appointment: { id: string; scheduledStart: string; serviceType: string } | null;
}

const statusConfig: Record<string, { label: string; badge: string; icon: typeof CheckCircle }> = {
    draft: { label: 'Borrador', badge: 'badge-warning', icon: Edit3 },
    ai_draft: { label: 'IA Borrador', badge: 'badge-primary', icon: AlertCircle },
    reviewed: { label: 'Revisado', badge: 'badge-info', icon: Clock },
    signed: { label: 'Firmado', badge: 'badge-success', icon: CheckCircle },
    amended: { label: 'Enmendado', badge: 'badge-neutral', icon: FileText },
};

export function EncountersListPage() {
    const { t } = useI18n();
    const navigate = useNavigate();
    const [encounters, setEncounters] = useState<Encounter[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('');

    useEffect(() => {
        loadEncounters();
    }, [statusFilter]);

    async function loadEncounters() {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set('status', statusFilter);
            params.set('limit', '50');
            const res = await fetch(`${API_BASE}/encounters?${params}`);
            const json = await res.json();
            setEncounters(json.data || []);
        } catch (err) {
            console.error('Failed to load encounters:', err);
        } finally {
            setLoading(false);
        }
    }

    const filtered = encounters.filter(enc => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        const patientName = `${enc.patient.firstName} ${enc.patient.lastName}`.toLowerCase();
        return patientName.includes(q) || enc.chiefComplaint?.toLowerCase().includes(q);
    });

    return (
        <div className="page-content fade-in">
            <div className="page-header" style={{ position: 'static', borderBottom: 'none', background: 'transparent', paddingLeft: 0 }}>
                <div>
                    <h1 className="page-title">{t('nav.encounters')}</h1>
                    <p className="page-subtitle">{filtered.length} {t('encounters.total') || 'encuentros registrados'}</p>
                </div>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-6)', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 240, position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
                    <input
                        className="form-input"
                        style={{ paddingLeft: 36 }}
                        placeholder="Buscar por paciente o motivo..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
                <select
                    className="form-input"
                    style={{ width: 180 }}
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                >
                    <option value="">Todos los estados</option>
                    <option value="draft">Borrador</option>
                    <option value="ai_draft">IA Borrador</option>
                    <option value="reviewed">Revisado</option>
                    <option value="signed">Firmado</option>
                    <option value="amended">Enmendado</option>
                </select>
            </div>

            {/* Table */}
            <div className="card">
                <div className="table-container">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Paciente</th>
                                <th>Motivo</th>
                                <th>Estado</th>
                                <th>Doctor</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-text-muted)' }}>Cargando...</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-text-muted)' }}>
                                    <FileText size={32} style={{ marginBottom: 8, opacity: 0.3 }} />
                                    <p>No se encontraron encuentros</p>
                                </td></tr>
                            ) : (
                                filtered.map(enc => {
                                    const cfg = statusConfig[enc.status] || statusConfig.draft;
                                    const StatusIcon = cfg.icon;
                                    return (
                                        <tr key={enc.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/encounters/${enc.id}`)}>
                                            <td style={{ fontWeight: 500 }}>
                                                {new Date(enc.appointment?.scheduledStart || enc.createdAt).toLocaleDateString('es-CO', {
                                                    day: '2-digit', month: 'short', year: 'numeric',
                                                })}
                                            </td>
                                            <td>{enc.patient.firstName} {enc.patient.lastName}</td>
                                            <td style={{ color: 'var(--color-text-secondary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {enc.chiefComplaint || enc.subjective?.substring(0, 60) || '—'}
                                            </td>
                                            <td>
                                                <span className={`badge ${cfg.badge}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                    <StatusIcon size={12} /> {cfg.label}
                                                </span>
                                            </td>
                                            <td style={{ color: 'var(--color-text-secondary)' }}>
                                                Dr. {enc.doctor.lastName}
                                            </td>
                                            <td>
                                                <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); navigate(`/encounters/${enc.id}`); }}>
                                                    {enc.status === 'signed' ? 'Ver' : 'Editar'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
